import React from "react";

import { openSupportChat } from "libs/support";
import { Link } from "ui/Link";
import styled from "styled-components";
import { PlatformTypes } from "shared/types/post";
import { useTrackEvent } from "libs/analytics";
import { CONTACT_SUPPORT_CONNECTING_ACCOUNT } from "constants/Events";
import { HubSpotHelpChatTopic } from "libs/support/open-support-chat";
import { Button } from "@/components/ui/button";

const platformNamesMapping = {
  [PlatformTypes.instagram]: "Instagram",
  [PlatformTypes.facebook]: "Facebook",
  [PlatformTypes.twitter]: "Twitter",
  [PlatformTypes.linkedin]: "LinkedIn",
  [PlatformTypes.googlemybusiness]: "Google My Business",
  [PlatformTypes.cluster]: "Cluster Post",
  [PlatformTypes.youtube]: "Youtube",
  [PlatformTypes.pinterest]: "Pinterest",
  [PlatformTypes.snapchat]: "SnapChat",
  [PlatformTypes.tiktok]: "Tiktok",
  [PlatformTypes.other]: "Other",
  [PlatformTypes.threads]: "Threads",
} satisfies Record<PlatformTypes, string>;

function getPlatformName(platform?: PlatformTypes): string {
  if (platform === undefined) {
    return "";
  }
  return platformNamesMapping[platform];
}

export function NeedHelpButton({
  children,
  platform,
  chatQuery,
  ...restProps
}: Omit<React.ComponentProps<typeof Link>, "as" | "onClick"> & {
  platform?: PlatformTypes;
  chatQuery?: HubSpotHelpChatTopic;
}) {
  const trackEvent = useTrackEvent();
  const platformName = getPlatformName(platform);

  const prefillMsg = platformName
    ? `Hey, I'm trying to connect my ${platformName} account and I'd like some help…`
    : "";

  const onButtonClicked = () => {
    openSupportChat(prefillMsg, chatQuery);
    if (platformName) {
      trackEvent({
        eventName: CONTACT_SUPPORT_CONNECTING_ACCOUNT,
        useServices: ["AMP"],
        metadata: {
          platformName,
        },
      });
    }
  };

  return (
    <ButtonWrap>
      <span>Need any help?</span>
      <Button
        variant="blue-secondary"
        size="xs"
        onClick={onButtonClicked}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(restProps as any)}
      >
        {children ?? "Contact Support"}
      </Button>
    </ButtonWrap>
  );
}

export const ButtonWrap = styled.div`
  display: flex;
  align-items: center;
  span {
    color: var(--Text-T01, #333);
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    margin-right: 8px;
  }
`;
